import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  ExpandMore,
  PermContactCalendar,
} from "@mui/icons-material";
import { FieldArray, FormikProps, useFormikContext } from "formik";
import { isNil } from "ramda";
import React, { memo } from "react";
import { Data } from "../../../store/merchant/types";
import pageStyles from "./../../../pages/pageStyles";
import MerchantContactForm from "./MerchantContactForm";
import OptionalToolTip from "../../../common/toolTip/OptionalToolTip";

export enum ContactType {
  PRIMARY = 1,
  SECONDARY = 2,
  OTHER = 3,
}

interface Contact {
  First?: string;
  Last?: string;
  Mobile?: number;
  Phone?: number;
  email?: string;
  SortNumber?: number;
}

interface CreateContactButtonProps {
  contacts: Contact[];
  onClickHandler: Function;
  disabled?: boolean;
}

interface FormProps {
  isUpdateAllowed?: boolean;
}

const createContact = (contactType: ContactType): Contact => {
  return {
    First: undefined,
    Last: undefined,
    Mobile: undefined,
    Phone: undefined,
    email: undefined,
    SortNumber: contactType,
  };
};

const CreateContactButton = ({
  contacts,
  onClickHandler,
  disabled,
}: CreateContactButtonProps): React.ReactElement => {
  const showPrimary = isNil(
    contacts?.find((c) => c.SortNumber === ContactType.PRIMARY)
  );
  const showSecondary = isNil(
    contacts?.find((c) => c.SortNumber === ContactType.SECONDARY)
  );

  if (showPrimary) {
    return (
      <Button
        color="primary"
        variant="contained"
        size="medium"
        endIcon={<Add />}
        onClick={() => onClickHandler(createContact(ContactType.PRIMARY))}
        disabled={disabled}
      >
        Create Primary Contact
      </Button>
    );
  } else if (showSecondary) {
    return (
      <Button
        color="primary"
        variant="contained"
        size="medium"
        endIcon={<Add />}
        onClick={() => onClickHandler(createContact(ContactType.SECONDARY))}
        disabled={disabled}
      >
        Create Secondary Contact
      </Button>
    );
  } else {
    return (
      <Button
        color="primary"
        variant="contained"
        size="medium"
        endIcon={<Add />}
        disabled={disabled}
        onClick={() => onClickHandler(createContact(ContactType.OTHER))}
      >
        Create Contact
      </Button>
    );
  }
};

const isContactPrimary = (contact: Contact) =>
  contact.SortNumber === ContactType.PRIMARY;

const isContactSecondary = (contact: Contact) =>
  contact.SortNumber === ContactType.SECONDARY;

const contactRank = ({ contact }: { contact: Contact }): string => {
  return (
    (isContactPrimary(contact) && "Primary Contact") ||
    (isContactSecondary(contact) && "Secondary Contact") ||
    "Contact"
  );
};

const EditMerchantContactComponent = (props: FormProps) => {
  const formikProps = useFormikContext();
  const { values } = formikProps as FormikProps<Data>;
  const styles = pageStyles();

  const { isUpdateAllowed } = props;
  // const isCreateAllowed = access?.C;
  // const isDeleteAllowed = access?.D;

  return (
    <FieldArray
      name="attributes.Contact"
      render={(arrayHelpers) => {
        return (
          <>
            {values.attributes?.Contact?.map((contact: Contact, index) => {
              return (
                <Accordion key={index} className={styles.Accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls={`${index}_content`}
                    id={`${index}_header`}
                  >
                    <Box className={styles.processorTitleWrapper}>
                      <Typography className={styles.processorTitle}>
                        <PermContactCalendar style={{ marginRight: "5px" }} />
                        {contactRank({ contact })}
                      </Typography>

                      {index + 1 === values?.attributes?.Contact?.length && (
                        <OptionalToolTip
                          tooltip={
                            isUpdateAllowed
                              ? null
                              : "Not applicable in this section"
                          }
                        >
                          <Button
                            color="secondary"
                            variant="outlined"
                            onClick={(): void => {
                              arrayHelpers.remove(index);
                            }}
                            size="small"
                            startIcon={<Delete />}
                            disabled={!isUpdateAllowed}
                          >
                            Delete
                          </Button>
                        </OptionalToolTip>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    style={{
                      background: "var(--dmg-bg)",
                      borderTop: "1px solid var(--dmg-gray-200)",
                      padding: "16px",
                      marginTop: "16px",
                    }}
                  >
                    <Grid container style={{ marginTop: "15px" }}>
                      <Grid item xs={12}>
                        <MerchantContactForm
                          disabled={!isUpdateAllowed}
                          index={index}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            <Grid item xs={12}>
              <Box textAlign="center" marginTop={"15px"}>
                {// @ts-ignore
                values?.attributes?.Contact?.length < 3 && (
                  <OptionalToolTip
                    tooltip={
                      isUpdateAllowed ? null : "Not applicable in this section"
                    }
                  >
                    <CreateContactButton
                      contacts={values?.attributes?.Contact as Contact[]}
                      onClickHandler={arrayHelpers.push}
                      disabled={!isUpdateAllowed}
                    />
                  </OptionalToolTip>
                )}
                {values?.attributes?.Contact?.length === 0 && (
                  <FormHelperText
                    error
                    style={{
                      textAlign: "center",
                      fontSize: "13px",
                      marginTop: "8px",
                    }}
                  >
                    Must have at least a Primary contact
                  </FormHelperText>
                )}
              </Box>
            </Grid>
          </>
        );
      }}
    />
  );
};

export default memo(EditMerchantContactComponent);
